/* eslint-disable react/jsx-key */
import React from 'react'
import { ArrowLeft, ChevronDown, ShoppingCart } from 'react-feather'
import Pill from 'src/components/common/ui-preview/skeletons/Pill'
import { OrderSummaryConfigType } from 'src/lib/types/customizeUI'
import { useLocale } from 'src/lib/hooks/useLocal'
import { classNames } from 'src/lib/utils/helpers'

interface OrderSummaryCardType {
  type: 'TILE' | 'HEADER'
  orderSummaryConfig: OrderSummaryConfigType
}

const OrderSummaryCard: React.FC<OrderSummaryCardType> = ({ type, orderSummaryConfig }) => {
  const { t } = useLocale()
  switch (type) {
    case 'TILE':
      return (
        <div
          className={classNames(
            'flex flex-col h-32 w-full gap-4 pt-3 px-3 pb-0 border-1 rounded-lg overflow-hidden bg-gradient-to-t from-black/[0.02] to-transparent',
            orderSummaryConfig.showOrderSummaryAs === 'TILE'
              ? 'border-primary-dark shadow-[0px_0px_0px_2.5px_rgba(1,204,167,0.25)]'
              : 'border-gray-200'
          )}
          tabIndex={0}
        >
          <div className='flex items-center gap-2'>
            <div
              className={classNames(
                'w-4 h-4 rounded-full border-1 flex-shrink-0 flex items-center justify-center',
                orderSummaryConfig.showOrderSummaryAs === 'TILE'
                  ? 'border-4 border-primary-dark bg-primary-dark'
                  : 'border-2 border-gray-300'
              )}
            >
              {orderSummaryConfig.showOrderSummaryAs === 'TILE' && (
                <div className='w-2 h-2 bg-white rounded-full'></div>
              )}
            </div>
            <span className='text-sm font-medium text-coal-dark'>{t('show_tile')}</span>
          </div>
          <div className='flex flex-col gap-2'>
            <div className='flex items-center bg-white border-1 border-gray-200 rounded-2xl px-3 h-14'>
              <div className='flex w-full items-center justify-between'>
                <div className='flex items-center space-x-2'>
                  <ShoppingCart className='w-4 h-4 text-gray-dark' />
                  <p className='text-sm font-medium text-coal-light'>{t('order_summary')}</p>
                </div>
                <div className='flex items-center gap-2'>
                  <p className='text-sm text-coal-light'>{t('items', { count: 3 })}</p>
                  <ChevronDown className='h-4 w-4 text-coal-dark' />
                </div>
              </div>
            </div>
            <div className='flex items-center bg-white border-1 border-gray-200 rounded-2xl px-3 h-14'>
              <div className='flex w-full items-center justify-between'>
                <div className='flex items-center space-x-2'>
                  <div className='w-4 h-4 rounded bg-gray-light'></div>
                  <div className='w-20 h-4 rounded bg-gray-light'></div>
                </div>
                <div className='flex items-center gap-2'>
                  <div className='w-12 h-4 rounded bg-gray-light'></div>
                  <div className='w-4 h-4 rounded bg-gray-light'></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    case 'HEADER':
      return (
        <div
          className={classNames(
            'flex flex-col h-32 w-full gap-4 pt-3 px-3 pb-0 border-1 rounded-lg overflow-hidden bg-gradient-to-t from-black/[0.02] to-transparent',
            orderSummaryConfig.showOrderSummaryAs === 'HEADER'
              ? 'border-primary-dark shadow-[0px_0px_0px_2.5px_rgba(1,204,167,0.25)]'
              : 'border-gray-200'
          )}
          tabIndex={0}
        >
          <div className='flex items-center gap-2'>
            <div
              className={classNames(
                'w-4 h-4 rounded-full border-2 bg-white flex-shrink-0 flex items-center justify-center',
                orderSummaryConfig.showOrderSummaryAs === 'HEADER'
                  ? 'border-4 border-primary-dark bg-primary-dark'
                  : 'border-gray-300'
              )}
            >
              {orderSummaryConfig.showOrderSummaryAs === 'HEADER' && (
                <div className='w-2 h-2 bg-white rounded-full'></div>
              )}
            </div>
            <span className='text-sm font-medium text-coal-dark'>Show on Header</span>
          </div>
          <div className='flex flex-col gap-0 border-1 bg-white border-gray-200 rounded-2xl overflow-visible'>
            <div className='flex items-center bg-transparent py-2 px-3 h-fit border-b-1 border-gray-200'>
              <div className='flex w-full items-center justify-between'>
                <div className='flex items-center space-x-2'>
                  <div className='w-11 h-11 rounded flex items-center justify-center bg-gray-light'>
                    <span className='font-medium text-gray-dark text-[10px]'>{t('LOGO')}</span>
                  </div>
                </div>
                <div className='flex flex-col items-end space-y-1'>
                  <div className='text-xs font-medium text-gray-dark'>{t('order_total')}</div>
                  <div className='flex items-center text-sm font-semibold text-gray-dark'>
                    {t('order_total_amount_rupee')}
                    <span className='font-normal text-xs'>{t('order_total_amount_paise')}</span>
                    <ChevronDown className='h-4 w-4 ml-1 text-gray-dark' />
                  </div>
                </div>
              </div>
            </div>
            <div className='flex items-center bg-transparent px-3 h-14'>
              <div className='flex w-full items-center justify-between'>
                <div className='flex items-center space-x-2'>
                  <div className='w-4 h-4 rounded bg-gray-light'></div>
                  <div className='w-20 h-4 rounded bg-gray-light'></div>
                </div>
                <div className='flex items-center gap-2'>
                  <div className='w-12 h-4 rounded bg-gray-light'></div>
                  <div className='w-4 h-4 rounded bg-gray-light'></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
  }
}

export default OrderSummaryCard
